import type { FieldConfig, VariableConfig } from "../fields/types";
import { UUID_V4_PATTERN } from "./uuid";
import { OLD_FIELD_ID_PATTERN } from "./fieldUtil";
import { isArray, isEmpty, isNil } from "lodash-es";
import type { FieldValueReferenceType } from "../fields/FieldValueReference";
import FieldValueReference from "../fields/FieldValueReference";
import type { Nullish } from "../types";
import { bounded } from "./regexUtil";
import VariableValueReference from "../variables/VariableValueReference";
import { renderFieldValueAsString } from "../fields/fieldValueRenderer";
import { hasData } from "../fields/fields";
import { isMetaVariable } from "../metaVariables/metaVariable";
import MetaVariableReference from "../metaVariables/MetaVariableReference";

export type Delimiter = "{" | "${";

type RawType = {
  type: "literal";
  value: string;
};
type FieldType = {
  type: "field";
  value: FieldValueReference;
};
type VariableType = {
  type: "variable";
  value: VariableValueReference;
};
type MetaVariableType = {
  type: "metaVariable";
  value: MetaVariableReference;
};
type UnknownType = {
  type: "unknown";
  value: string;
};

export type StringTemplateTypes = RawType | FieldType | VariableType | MetaVariableType | UnknownType;
export type StringTemplate = StringTemplateTypes[];

export const parseStringTemplateForField = (
  input: Nullish<string> | StringTemplate,
  fields: FieldConfig[],
  variables: VariableConfig[],
) => (isArray(input) ? input : parseStringTemplate(input, fields, variables));

const IMAGE_PLACEHOLDER_PREFIX = "img:";

export const parseStringTemplate = (
  input: Nullish<string>,
  fields: FieldConfig[],
  variables: VariableConfig[],
  delimiter: Delimiter = "${",
): StringTemplate => {
  if (isNil(input)) {
    return [];
  }

  // Captures the placeholder in the src tag
  const imgTagWithPlaceholder =
    delimiter === "${"
      ? /<img\b[^>]*?\bsrc=["']\$\{([\w-]+)}["'][^>]*?\/?>/g
      : /<img\b[^>]*?\bsrc=["']\{([\w-]+)}["'][^>]*?\/?>/g;
  const onAnyValueWrappedWithDelimiters = delimiter === "${" ? /(\${[^{}]*})/ : /({[^{}]*})/;
  // Matches data names, (old) field ids and nesting
  const validPlaceholder = delimiter === "${" ? /\${(img:)?[\w.-]+}/ : /\{(img:)?[\w.-]+}/;

  return (
    input
      // Replace img tags that include a placeholder with a placeholder prefixed with "img:", so we can differentiate it
      // from a regular placeholder below. (e.g. <img src="${photo}"> → ${img:photo})
      .replace(imgTagWithPlaceholder, (_match, placeholder) => `${delimiter}${IMAGE_PLACEHOLDER_PREFIX}${placeholder}}`)
      .split(onAnyValueWrappedWithDelimiters)
      .filter(Boolean)
      .map((value) => {
        if (bounded(validPlaceholder).test(value)) {
          const valueWithoutDelimiter = value.slice(delimiter.length, -1); // Strip '${' or '{' prefix and '}' suffix
          const isImagePlaceholder = valueWithoutDelimiter.startsWith(IMAGE_PLACEHOLDER_PREFIX);
          const normalizedValue = isImagePlaceholder
            ? valueWithoutDelimiter.slice(IMAGE_PLACEHOLDER_PREFIX.length)
            : valueWithoutDelimiter;
          const referenceType = isImagePlaceholder ? "image" : undefined;

          if (isMetaVariable(normalizedValue)) {
            return { type: "metaVariable" as const, value: MetaVariableReference.from(normalizedValue) };
          } else {
            const [referenceIdOrDataName, ...sub] = normalizedValue.split(".");
            if (UUID_V4_PATTERN.test(referenceIdOrDataName) || OLD_FIELD_ID_PATTERN.test(referenceIdOrDataName)) {
              const field = fields.find((field) => field.uid.equals(referenceIdOrDataName));
              if (!isNil(field)) {
                return fieldToMixedValue(normalizedValue, sub, field, referenceType);
              }
              const variable = variables.find((variable) => variable.uid.equals(referenceIdOrDataName));
              return variableToMixedValue(normalizedValue, variable);
            } else {
              // If it's not a variable or uuid based reference then it must be a dataname
              const field = fields.find((field) => field.properties.data_name === referenceIdOrDataName);
              if (!isNil(field)) {
                return fieldToMixedValue(normalizedValue, sub, field, referenceType);
              }
              const variable = variables.find((variable) => variable.dataName === referenceIdOrDataName);
              return variableToMixedValue(normalizedValue, variable);
            }
          }
        } else {
          return { type: "literal" as const, value };
        }
      })
      // Join any adjacent literal types
      .reduce((acc, item) => {
        const lastItem = acc[acc.length - 1];
        if (item.type === "literal" && lastItem?.type === "literal") {
          lastItem.value += item.value;
        } else {
          acc.push(item);
        }

        return acc;
      }, [] as StringTemplate)
  );
};

const fieldToMixedValue = (
  value: string,
  sub: string[],
  field?: FieldConfig,
  referenceType?: FieldValueReferenceType,
): StringTemplateTypes =>
  isNil(field)
    ? { type: "unknown", value }
    : {
        type: "field",
        value: new FieldValueReference(field.uid, sub, referenceType),
      };

const variableToMixedValue = (value: string, variable?: VariableConfig): StringTemplateTypes =>
  isNil(variable)
    ? { type: "unknown", value }
    : {
        type: "variable",
        value: new VariableValueReference(variable.uid),
      };

export const stringTemplateToString = (
  values: StringTemplate,
  fields: FieldConfig[],
  variables: VariableConfig[],
  delimiter: Delimiter = "${",
  separator = "",
  allowImgTags = false,
): string => stringTemplateToStringArray(values, fields, variables, delimiter, true, allowImgTags).join(separator);

export const stringTemplateToStringArray = (
  values: StringTemplate,
  fields: FieldConfig[],
  variables: VariableConfig[],
  delimiter: Delimiter = "${",
  wrapFieldsWithDelimiter = false,
  allowImgTags = false,
): string[] =>
  values.map(({ type, value }) => {
    switch (type) {
      case "literal":
        return value;
      case "metaVariable":
      case "unknown":
        return delimiter + value + "}";
      case "field":
        const field = fields.find((field) => field.uid.equals(value.id));
        const dataName = isNil(field?.properties.data_name) ? value : (field.properties.data_name as string);
        const placeholder = isEmpty(value.path) ? dataName : `${dataName}.${value.path.join(".")}`;
        if (allowImgTags && value.type === "image") {
          return `<img src="${delimiter}${placeholder.toString()}}">`;
        }
        return wrapFieldsWithDelimiter ? delimiter + placeholder.toString() + "}" : placeholder.toString();
      case "variable":
        const variable = variables.find((variable) => variable.uid.equals(value.id));
        const variableDataName = isNil(variable?.dataName) ? value : variable?.dataName;
        return wrapFieldsWithDelimiter ? delimiter + variableDataName.toString() + "}" : variableDataName.toString();
    }
  });

export const splitLiteralsOnComma = (value: StringTemplate) =>
  value.flatMap((item) => {
    if (item.type !== "literal") {
      return item;
    }

    return item.value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => !isEmpty(v))
      .map((v) => ({ type: "literal", value: v })) as StringTemplate;
  });

export const renderStringTemplate = (
  value: StringTemplate,
  fieldConfigs: FieldConfig[],
  data: Record<string, unknown>,
) =>
  value
    .map((item) => {
      if (item.type === "literal") {
        return item.value;
      } else if (item.type === "field") {
        const fieldConfig = fieldConfigs.find((fieldConfig) => fieldConfig.uid.equals(item.value.id));
        if (!isNil(fieldConfig) && hasData(fieldConfig)) {
          return renderFieldValueAsString(data[fieldConfig.properties.data_name], item.value.path, fieldConfig);
        }
      } else if (item.type === "variable") {
        // TODO, DEV-7425 Implement
      } else if (item.type === "metaVariable") {
        // TODO, DEV-7425 Implement
      }
    })
    .join("");
